<template>
  <div>
    <div
      ref="tabsContainer"
      class="general-tabs-container w-100"
      :class="[shadowPositionClass, bgClass ? bgClass : 'bg-dark']"
    >
      <div
        class="tabs-container border-color-light-gray d-flex flex-row justify-content-start"
        :class="borderClass ? 'border-bottom' : ''"
      >
        <div
          @click="$emit('select-tab', tab.key)"
          v-for="(tab, tabIndex) in tabs"
          :key="tab.key"
          class="tab-item rounded-top"
          :style="`width: ${tabItemWidth}px`"
          :class="
            tab.key == selectedTab
              ? 'tab-item-selected ' +
                (borderClass ? borderClass : '') +
                ' bg-extra-light-gray'
              : ' bg-transparent'
          "
        >
          <div
            class="d-flex flex-row justify-content-between align-items-center"
            :class="
              tab.key == selectedTab
                ? ''
                : tabIndex == tabs.length - 1
                ? ''
                : 'border-dark-gray only-border-right'
            "
          >
            <div
              class="d-flex flex-row justify-content-start align-items-center tab-title"
              :style="`max-width: ${tabItemWidth - 20}px`"
            >
              <div
                class="little-circle rounded-circle ml-2 mr-2 border border-med-light-gray"
                :class="
                  tab.key == selectedTab
                    ? 'bg-primary'
                    : itemBGClass
                    ? itemBGClass
                    : 'bg-white'
                "
              ></div>
              <span
                class="mr-2 text-nowrap"
                :class="
                  tab.key == selectedTab
                    ? ''
                    : itemTextClass
                    ? itemTextClass
                    : 'text-white'
                "
                >{{ tab.text }}</span
              >
            </div>
            <b-icon-x
              v-if="tab.closeable"
              class="mr-1 text-nowrap close-icon"
              :class="
                tab.key == selectedTab
                  ? ''
                  : itemTextClass
                  ? itemTextClass
                  : 'text-white'
              "
              @click.prevent.stop="removeTab(tabIndex)"
              >X</b-icon-x
            >
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="(tab, tabIndex) in tabs"
      :key="tab.key"
      :class="componentsClass ? componentsClass : ''"
      v-show="tab.key == selectedTab"
    >
      <component
        :is="tab.component"
        v-bind="tab.props"
        v-on="$listeners"
        @add-tab="addTab"
        @remove-current-tab="removeCurrentTab(tabIndex)"
        @update-current-tab="updateCurrentTab(tabIndex, $event)"
        :showing="tab.key == selectedTab && showing"
        :tabsHeight="tabsContainerHeight"
        ref="tab-components"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
const maxTabWidth = 220;
export default {
  name: "Tabs",
  props: [
    "tabs",
    "selectedTab",
    "showing",
    "bgClass",
    "borderClass",
    "itemTextClass",
    "itemBGClass",
    "shadowPosition",
    "componentsClass"
  ],
  data() {
    return {
      tabsContainerHeight: 0,
      tabsContainerWidth: 0
    };
  },
  mounted() {
    this.computeTabsContainerWidth();
    window.addEventListener("resize", this.computeTabsContainerWidth);
  },
  destroyed() {
    window.removeEventListener("resize", this.computeTabsContainerWidth, true);
  },
  methods: {
    addTab(tabElement) {
      this.$emit("add-tab", tabElement);
      this.computeTabsContainerHeight();
    },
    removeCurrentTab(tabIndex) {
      this.$emit("remove-tab", tabIndex);
      this.computeTabsContainerHeight();
    },
    removeTab(tabIndex) {
      this.$emit("remove-tab", tabIndex);
      this.computeTabsContainerHeight();
    },
    updateCurrentTab(tabIndex, data) {
      this.$emit("update-tab", { tabIndex, data });
      this.computeTabsContainerHeight();
    },
    computeTabsContainerHeight() {
      setTimeout(() => {
        const tabsContainerHeight = this.$refs.tabsContainer.clientHeight;
        this.tabsContainerHeight = tabsContainerHeight;
        this.$emit("tabs-container-height-change", tabsContainerHeight);
      }, 0);
    },
    computeTabsContainerWidth() {
      if (this.$refs.tabsContainer) {
        setTimeout(() => {
          const tabsContainerWidth = this.$refs.tabsContainer.clientWidth;
          this.tabsContainerWidth = tabsContainerWidth;
        }, 0);
      }
    },
    refreshShowingTab() {
      const index = this.tabs.findIndex(t => t.key == this.selectedTab);
      try {
        this.$refs["tab-components"][index].refreshData();
      } catch (e) {
        console.log(e);
      }
    }
  },
  computed: {
    ...mapState("layout", {
      sidebarChange: state => state.sidebarChange
    }),
    tabItemWidth() {
      return Math.min(maxTabWidth, this.tabsContainerWidth / this.tabs.length);
    },
    shadowPositionClass() {
      switch (this.shadowPosition) {
        case "top":
          return "shadow-top";
        case "top-left":
          return "shadow-top-left";
        default:
          return "";
      }
    }
  },
  watch: {
    showing: function(newValue) {
      if (newValue) {
        this.computeTabsContainerWidth();
      }
    },
    sidebarChange: function(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.computeTabsContainerWidth();
        }, 500);
      }
    }
  }
};
</script>

<style scoped>
.tab-item {
  margin-bottom: -1px;
  padding-top: 8px;
  height: 35px;
  font-size: 0.8rem;
  transition: width 0.5s;
}
.tab-item-selected {
  box-shadow: 0px -1px 5px -3px rgb(0 0 0 / 75%);
  margin-left: -1px;
}
.little-circle {
  width: 12px;
  height: 12px;
  min-width: 12px;
}
.tab-item:hover {
  cursor: pointer;
}
.general-tabs-container {
  overflow-x: auto;
  overflow-y: hidden;
  padding-top: 8px;
  padding-left: 8px;
}
.bg-dark {
  background-color: #272930 !important;
}
.close-icon {
  font-size: 20px;
}
.bordered-tab-item {
  border-top: 1px solid;
  border-left: 1px solid;
  border-right: 1px solid;
}
.tab-title {
  overflow-x: hidden;
}
.only-border-right {
  border-right: 1px solid;
}
.left-white-border {
  border-left: 8px solid white;
  height: calc(100vh - 257px);
  overflow-x: hidden !important;
}
.tabs-container {
  box-shadow: inset 0px -10px 10px -16px rgb(0 0 0 / 75%);
}

.shadow-top {
  box-shadow: inset 0px 16px 10px -16px rgb(0 0 0 / 75%);
}
.shadow-top-left {
  box-shadow: inset 16px 16px 10px -16px rgb(0 0 0 / 75%);
}
</style>
